/**
 * Shared category cascade helpers for ticket forms.
 * Numeric id compare avoids string/number mismatches after API reload.
 */
import {
  ensureSelectedCategoryInOptions,
  filterLevelOneCategories
} from './category-visibility'

const LEVEL_OPTION_KEYS = [
  null,
  'lvlOneOptions',
  'lvlTwoOptions',
  'lvlThreeOptions',
  'lvlFourOptions',
  'lvlFiveOptions',
  'lvlSixOptions'
]

const LEVEL_FIELD_KEYS = [
  null,
  'categoryLv1',
  'categoryLv2',
  'categoryLv3',
  'categoryLv4',
  'categoryLv5',
  'categoryLv6'
]

export function emptyLvlOptions () {
  return {
    lvlOneOptions: [],
    lvlTwoOptions: [],
    lvlThreeOptions: [],
    lvlFourOptions: [],
    lvlFiveOptions: [],
    lvlSixOptions: []
  }
}

/** Deepest non-empty categoryLv* on a ticket-like object. */
export function getDeepestCategoryId (ticketLike) {
  if (!ticketLike) return null
  let result = null
  for (let level = 1; level <= 6; level += 1) {
    const value = ticketLike[LEVEL_FIELD_KEYS[level]]
    if (value != null && value !== '') {
      result = value
    }
  }
  return result
}

/**
 * Children of parentId (numeric-safe).
 * @param {object[]} categories
 * @param {number|string|null|undefined} parentId
 * @param {Map<number, object[]>|null} [childrenByParent]
 */
export function findChildCategories (categories, parentId, childrenByParent = null) {
  if (parentId == null || parentId === '') return []
  const parent = Number(parentId)
  if (!Number.isFinite(parent)) return []
  if (childrenByParent) {
    return childrenByParent.get(parent) || []
  }
  if (!Array.isArray(categories)) return []
  return categories.filter((item) => Number(item.parentId) === parent)
}

/** One-pass index: parentId → children[] */
export function indexCategoriesByParent (categories) {
  const map = new Map()
  if (!Array.isArray(categories)) return map
  for (let i = 0; i < categories.length; i += 1) {
    const item = categories[i]
    const parent = Number(item?.parentId)
    if (!map.has(parent)) map.set(parent, [])
    map.get(parent).push(item)
  }
  return map
}

/** One-pass index: id → category */
export function indexCategoriesById (categories) {
  const map = new Map()
  if (!Array.isArray(categories)) return map
  for (let i = 0; i < categories.length; i += 1) {
    const item = categories[i]
    const id = Number(item?.id)
    if (Number.isFinite(id)) map.set(id, item)
  }
  return map
}

/**
 * Rebuild cascade select options + categoryLv* fields from the deepest selected id.
 * @returns {{ lvlOptions: object, levels: object }}
 */
export function buildCategoryCascadeState (categories, leafCategoryId, userGroupId) {
  const lvlOptions = emptyLvlOptions()
  lvlOptions.lvlOneOptions = filterLevelOneCategories(categories, userGroupId)
  const levels = {
    categoryLv1: null,
    categoryLv2: null,
    categoryLv3: null,
    categoryLv4: null,
    categoryLv5: null,
    categoryLv6: null
  }

  if (!Array.isArray(categories) || categories.length === 0) {
    return { lvlOptions, levels }
  }
  if (leafCategoryId == null || leafCategoryId === '') {
    return { lvlOptions, levels }
  }

  const byId = indexCategoriesById(categories)
  const byParent = indexCategoriesByParent(categories)
  const leaf = byId.get(Number(leafCategoryId))
  if (!leaf) {
    return { lvlOptions, levels }
  }

  // Ancestors from leaf → root (same order as previous filterParentCategory walk)
  const chain = []
  let current = leaf
  const seen = new Set()
  while (current && !seen.has(Number(current.id))) {
    chain.push(current)
    seen.add(Number(current.id))
    const parentId = Number(current.parentId)
    if (!Number.isFinite(parentId) || parentId === 0 || !byId.has(parentId)) {
      break
    }
    current = byId.get(parentId)
  }

  for (let i = 0; i < chain.length; i += 1) {
    const item = chain[i]
    const level = Number(item?.level)
    const fieldKey = LEVEL_FIELD_KEYS[level]
    const optionKey = LEVEL_OPTION_KEYS[level]
    if (fieldKey) {
      levels[fieldKey] = item.id
    }
    if (level === 1) {
      lvlOptions.lvlOneOptions = filterLevelOneCategories(categories, userGroupId)
    } else if (optionKey) {
      lvlOptions[optionKey] = byParent.get(Number(item.parentId)) || []
    }
  }

  // Legacy tickets may keep L1 after visibelGroup was removed for this group.
  // Inject selected L1 into options so the select shows the name, not the id.
  lvlOptions.lvlOneOptions = ensureSelectedCategoryInOptions(
    lvlOptions.lvlOneOptions,
    levels.categoryLv1,
    categories
  )

  return { lvlOptions, levels }
}

export default {
  emptyLvlOptions,
  getDeepestCategoryId,
  findChildCategories,
  indexCategoriesByParent,
  indexCategoriesById,
  buildCategoryCascadeState
}
